<template>
  <owa-modal-frame
    ref="frame"
    :title="modalTitle"
    has-actions
    :is-showing="isShowing"
    @close-modal="$emit('close-modal')"
  >
    <screen-paths-to-stay
      v-if="currentScreen === 'paths-to-stay'"
      :account-email="accountEmail"
      :plan-name="planName"
      :subscription-end-date="periodEndAtDate"
      @save-path-click="onSavePathClick"
    />

    <screen-confirm-cancellation
      v-else-if="currentScreen === 'confirm-cancellation'"
      :subscription-end-date="periodEndAtDate"
      :account-email="accountEmail"
      :plan-name="planName"
      :has-perpetual-license="hasPerpetualLicense"
      :ends-immediately="endsImmediately"
      :cancel-error="cancelError"
    />

    <screen-why-are-you-canceling
      v-else-if="currentScreen === 'why-canceling'"
      ref="whyAreYouCanceling"
      :is-processing="isProcessing"
      :cancel-error="cancelError"
      @submit-readiness-changed="onSubmitReadinessChanged"
    />

    <template #actions>
      <ul
        class="display-flex flex-column flex-items-center tablet:flex-row tablet:flex-content-between tablet:flex-row-reverse"
        style="--list-style-type: none; --list-padding-left: 0; --button-margin-bottom: 0; gap: var(--spacing-md)"
      >
        <template v-if="currentScreen === 'paths-to-stay'">
          <li>
            <owa-action-button
              key="continue"
              label="Continue"
              theme="danger"
              @click="onContinue"
            />
          </li>
          <li>
            <button
              type="button"
              data-autofocus
              class="flat"
              @click="$emit('close-modal')"
            >
              Never mind
            </button>
          </li>
        </template>

        <template v-else-if="currentScreen === 'confirm-cancellation'">
          <li>
            <owa-action-button
              key="continue-to-cancellation"
              label="Continue to cancellation"
              theme="danger"
              @click="onAdvanceToReason"
            />
          </li>
          <li>
            <button
              type="button"
              data-autofocus
              class="flat"
              @click="onBack"
            >
              &larr; Back
            </button>
          </li>
        </template>

        <template v-else-if="currentScreen === 'why-canceling'">
          <li>
            <owa-action-button
              v-if="!cancelError"
              key="cancel-subscription"
              :is-disabled="!submitReady"
              :is-processing="isProcessing"
              icon-name="faBan"
              is-processing-label="Canceling Subscription..."
              label="Cancel Subscription"
              theme="danger"
              @click="cancelSubscription"
            />
            <owa-action-button
              v-else
              key="try-again"
              :is-processing="isProcessing"
              is-processing-label="Trying again..."
              label="Try again"
              theme="danger"
              @click="cancelSubscription"
            />
          </li>
          <li>
            <button
              type="button"
              data-autofocus
              class="flat"
              @click="onBackFromReason"
            >
              &larr; Back
            </button>
          </li>
        </template>
      </ul>
    </template>
  </owa-modal-frame>
</template>

<script>
  import { mapActions } from 'vuex'
  import get from 'lodash/get'
  import moment from 'moment'
  import { ModalFrameMixin } from 'app/components-wa/modals/ModalFrame'
  import ScreenPathsToStay from 'app/components-wa/modals/cancel-subscription/ScreenPathsToStay'
  import ScreenConfirmCancellation from 'app/components-wa/modals/cancel-subscription/ScreenConfirmCancellation'
  import ScreenWhyAreYouCanceling from 'app/components-wa/modals/cancel-subscription/ScreenWhyAreYouCanceling'

  const formatEndDate = (endsAt) => (endsAt ? moment.utc(endsAt).format('MMMM D, YYYY') : '')

  const periodEndMessage = (endDate) =>
    `Your subscription has been canceled and won't auto-renew. You'll continue to get updates, services, and goodies until it expires on ${endDate}. Changed your mind? You can turn auto-renew back on anytime before then.`

  export default {
    name: 'ModalCancelSubscription',

    components: {
      ScreenPathsToStay,
      ScreenConfirmCancellation,
      ScreenWhyAreYouCanceling
    },

    mixins: [ModalFrameMixin],

    props: {
      subscription: {
        type: Object,
        required: true
      },
      flashTopic: {
        type: String,
        default: 'billing:section'
      }
    },

    emits: ['close-modal'],

    data() {
      return {
        currentScreen: 'paths-to-stay',
        isProcessing: false,
        cancelError: null,
        retryAttempt: 0,
        submitReady: false
      }
    },

    computed: {
      modalTitle() {
        switch (this.currentScreen) {
          case 'paths-to-stay':
            return 'Before you cancel'
          case 'confirm-cancellation':
            return "Here's what changes"
          case 'why-canceling':
            return 'Why are you canceling?'
          default:
            return ''
        }
      },

      accountEmail() {
        return get(this, ['$account', 'email']) || ''
      },

      planName() {
        return get(this.subscription, ['plan', 'name']) || ''
      },

      hasPerpetualLicense() {
        // Strict === true so missing/undefined flags default to false (honest fallback).
        return get(this.subscription, ['plan', 'perpetualLicenseEnabled']) === true
      },

      // An unpaid Stripe cancel ends the subscription now (no dunning, no access to period end).
      // Braintree past_due still cancels at period end.
      endsImmediately() {
        return get(this.subscription, ['type']) === 'stripe' && ['past_due', 'unpaid'].includes(get(this.subscription, ['status']))
      },

      // `coverageEndedAt` is the date they're paid through. It equals
      // `periodEndAt` on a current subscription, but a past_due one has had its
      // cycle rolled forward onto the period it failed to pay for, so the raw
      // field would tell this customer their access runs a whole extra cycle.
      periodEndAtDate() {
        return formatEndDate(get(this.subscription, ['coverageEndedAt']) || get(this.subscription, ['periodEndAt']))
      },

      // A past_due subscription's paid-through date has already gone by, so the
      // usual "you'll keep everything until <date>, renew anytime before then"
      // promised access until a day in the past and a window that had closed.
      // Stripe past_due ends immediately instead; this still serves Braintree.
      canceledMessage() {
        if (get(this.subscription, ['status']) === 'past_due') {
          return `Your subscription has been canceled and won't auto-renew. It was paid through ${this.periodEndAtDate}. Changed your mind? You can turn auto-renew back on.`
        }

        return periodEndMessage(this.periodEndAtDate)
      }
    },

    watch: {
      isShowing(showing) {
        if (showing) {
          this.resetState()
          this.captureSafe('cancel_modal:opened', {})
        }
      },

      // Screen changes remove the focused button, so re-apply initial focus.
      currentScreen() {
        this.$nextTick(() => this.$refs.frame && this.$refs.frame.focusFirstElement())
      }
    },

    mounted() {
      if (this.isShowing) {
        this.captureSafe('cancel_modal:opened', {})
      }
    },

    methods: {
      ...mapActions('subscriptions', ['cancelAtPeriodEnd']),

      ...mapActions('flashMessages', ['addFlashMessage']),

      captureSafe(eventName, payload) {
        if (this.$posthog && typeof this.$posthog.capture === 'function') {
          this.$posthog.capture(eventName, payload)
        }
      },

      resetState() {
        this.currentScreen = 'paths-to-stay'
        this.isProcessing = false
        this.cancelError = null
        this.retryAttempt = 0
        this.submitReady = false
      },

      onContinue() {
        this.captureSafe('cancel_modal:continue_clicked', {})
        this.currentScreen = 'confirm-cancellation'
      },

      onBack() {
        if (this.isProcessing) return
        this.cancelError = null
        this.currentScreen = 'paths-to-stay'
      },

      onAdvanceToReason() {
        // Screen 2 -> Screen 3. The actual Stripe cancellation now fires from
        // Screen 3's primary action (see cancelSubscription below).
        this.captureSafe('cancel_modal:reason_screen_opened', {})
        this.cancelError = null
        this.submitReady = false
        this.currentScreen = 'why-canceling'
      },

      onBackFromReason() {
        if (this.isProcessing) return
        this.cancelError = null
        this.submitReady = false
        this.currentScreen = 'confirm-cancellation'
      },

      onSubmitReadinessChanged(ready) {
        this.submitReady = !!ready
      },

      onSavePathClick({ key, secondary }) {
        this.captureSafe('cancel_modal:save_path_clicked', { path: key, secondary })
      },

      async cancelSubscription() {
        // Read before the request: the response replaces `subscription`.
        const requestedEndDate = this.periodEndAtDate
        const expectedImmediate = this.endsImmediately

        const isRetry = this.cancelError !== null

        if (isRetry) {
          this.retryAttempt += 1
          this.cancelError = null
        }

        this.isProcessing = true

        const reasonPayload = this.$refs.whyAreYouCanceling && this.$refs.whyAreYouCanceling.collectPayload()

        try {
          const updated = await this.cancelAtPeriodEnd({
            cancelAtPeriodEnd: true,
            reasonId: reasonPayload ? reasonPayload.reasonId : null,
            comment: reasonPayload ? reasonPayload.comment : null
          })

          // The server decides on Stripe's status, which our copy of the status can lag,
          // so describe what actually happened rather than what we expected. A period-end
          // cancel on a row we thought was past_due means Stripe found it paid, so it runs
          // to the period end the server returns, not the paid-through date we derived.
          let canceledMessage = this.canceledMessage

          if (get(updated, ['status']) === 'canceled') {
            canceledMessage = "Your subscription has been canceled and access has ended. We've stopped collecting the unpaid renewal."
          } else if (expectedImmediate) {
            canceledMessage = periodEndMessage(formatEndDate(get(updated, ['periodEndAt'])) || requestedEndDate)
          }

          this.captureSafe('cancel_modal:cancel_completed', {})
          this.cancelError = null

          this.addFlashMessage({
            message: {
              title: 'Subscription Canceled',
              content: canceledMessage,
              iconName: 'faCheck',
              theme: 'success',
              styleOverride: { '--message-background': 'var(--green1);' }
            },
            topic: this.flashTopic
          })

          this.isProcessing = false
          this.$emit('close-modal')
        } catch (err) {
          this.captureSafe('cancel_modal:cancel_failed', {
            retry_attempt: this.retryAttempt
          })

          this.cancelError = err
          this.isProcessing = false
        }
      }
    }
  }
</script>
